import { AlertService } from 'src/app/diagnostics/component/alert/alert.service';
import { AlertHit, alertHitScopes, loadAlertHits } from '../confidence-chart/issue-alert-hits';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input,
  OnDestroy, OnInit, Output, ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { shareReplay, take, takeUntil } from 'rxjs/operators';
import { IssuesStateService } from '../../issues-state.service';
import { IssuesService } from '../../issues.service';
import { AlertCaseComponent } from 'src/app/diagnostics/component/alert-case/alert-case.component';
import { AlertDetailService } from 'src/app/diagnostics/component/alert-detail/alert-detail.service';
import {
  CaseWorkflowContext, issueCaseWorkflowContext,
} from 'src/app/diagnostics/component/alert-case/case-workflow-context';
import type { AlertDetailDialogData } from 'src/app/service-center/alert-detail-dialog/alert-detail-dialog.component';
import { AlertTriageOpener } from 'src/app/service-center/alert-detail-dialog/alert-triage-opener';
import {
  DvirDetailDialogComponent, DvirDetailDialogData,
} from 'src/app/fleet/dvir-detail/dvir-detail-dialog.component';
import { severityLabelKey } from 'src/app/service-center/severity';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';
import { AssetFaultService } from 'src/app/services/asset-fault.service';
import { AssetFault } from 'src/app/models/assetFault';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { AuthService } from 'src/app/services/auth.service';
import { MaintainVehicle, ViewVehicle, ViewAlert } from 'src/app/models/constant/right';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import {
  FeatureSettings, TenantFeatures, isFeatureEnabled, issueCasesEnabled,
} from 'src/app/models/featureSettings';
import { IssueDto } from '../../models/issue';
import { ContributionDto, ContributionSourceTypes } from '../../models/contribution';
import {
  EvidenceGroupVm, IssueDetailLayout, IssueDetailLayouts, IssueRowVm, IssueTimelineEntryVm,
  WriteStatus, WriteStatuses,
} from '../../models/issue-view';
import {
  SUPPRESSION_DAYS, DispositionRequest, DispositionTypes, OperatorDispositionType,
} from '../../models/disposition';
import {
  buildTimeline, displayConfidencePct, groupEvidence, isConfidenceMuted, isDvirOrigin,
  isReopened, isUnsetDate, lastActivityUtc, manualReporterNames,
} from '../../issue-formatting';
import { buildConfidenceSeries, ConfidenceSeriesVm } from '../confidence-chart/confidence-series';
import {
  SuppressDialogComponent, SuppressDialogResult,
} from '../suppress-dialog/suppress-dialog.component';
import {
  FaultHistoryDialogComponent, FaultHistoryDialogData,
} from '../fault-history-dialog/fault-history-dialog.component';
import { VehicleDetailService } from 'src/app/fleet/vehicle-detail/vehicle-detail.service';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { GoogleMapService } from 'src/app/services/google-map.service';
import { vehicleMapEvent } from 'src/app/fleet/vehicle-actions';
import { VehicleState } from 'src/app/models/vehicleState';
import { IssueLocationStates } from '../../query/issue-fleet-row';

// The body of an Issue: what fired, how the work is coordinated, and how it closes.
//
// One component for every surface that explains an Issue — the vehicle tab's accordion and
// the fleet list's drawer. An Issue explained two different ways in two places is the thing
// this prevents.
@Component({
  selector: 'app-issue-detail',
  host: { '[class.issue-drawer-layout]': 'drawerLayout' },
  templateUrl: './issue-detail.component.html',
  styleUrls: ['./issue-detail.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssueDetailComponent implements OnInit, OnDestroy {
  @Input() issueId!: string;
  @Input() assetId!: string;
  @Input() assetGroups: any[] = [];
  // The row the caller already had. Seeding from it paints the detail immediately
  // instead of leaving the panel blank until the first read returns.
  @Input() seed?: IssueRowVm;
  // A partial Issue for callers that hold the projected row rather than the full
  // document — the fleet sheet. The header paints from it at once; the evidence and
  // the timeline fill in when the read returns.
  @Input() seedIssue?: IssueDto;
  @Input() canMaintain = false;
  // History. The Issue is closed, so the work it describes is finished: the panel reads
  // it out and offers nothing to do to it. This is separate from the rights inputs — a
  // user who may act on open Issues still has nothing to act on here.
  @Input() readOnly = false;

  // The host closes the panel or collapses the vehicle card.
  @Output() closeRequested = new EventEmitter<void>();
  // The Issues hosts share this header. Other hosts can supply their own.
  @Input() showHeader = false;
  // A host that is itself the window (the alert triage dialog) has nothing for it to close.
  @Input() showClose = true;
  @Input() closeAsCollapse = false;
  // Coordination opens with the panel. In a drawer the section toggle buries the case
  // work a second level deep; on the vehicle tab the accordion already is that level.
  @Input() startOpen = false;
  // Whether the header may offer the asset as a link. Only a host that is NOT already
  // scoped to that asset sets it: the fleet sheet does, the asset page's own accordion does
  // not, because there the link points at the screen the reader is on.
  @Input() canOpenAsset = false;
  // One "Details" row over everything above the footer, for a host that opens on the
  // verdict — the triage dialog. Hosts that leave it off render the body as they always did.
  @Input() collapsibleBody = false;
  // The Issues drawer and vehicle card use the same presentation.
  @Input() layout: IssueDetailLayout = IssueDetailLayouts.Classic;

  get drawerLayout(): boolean { return this.layout === IssueDetailLayouts.Drawer; }

  // Where the asset is now, as the sheet's row knew it: the shop's name, or the location
  // state when there is no shop to name. The Issue document carries neither, so the host
  // that holds the row passes them in.
  @Input() locationLabel = '';
  @Input() locationState = '';

  // The asset's live state, for the position the map opens on. Read once per drawer open
  // and only in the drawer layout, which is the only layout that shows the row.
  vehicleState: VehicleState | null = null;

  // Empty for the unset date a state without a fix carries, so the row never says
  // "position" with nothing after it.
  get positionAtUtc(): string {
    const at = this.vehicleState?.locationUpdateDateTimeUtc;
    const iso = at ? String(at) : '';
    return isUnsetDate(iso) ? '' : iso;
  }

  get locationText(): string {
    if (this.locationLabel) return this.locationLabel;
    if (this.locationState === IssueLocationStates.Road) return this.translate.instant('@otr');
    // The same fallback the row uses while the shop's group name has not loaded.
    if (this.locationState === IssueLocationStates.Shop) return this.translate.instant('@issueAtShop');
    return '';
  }

  get showLocationRow(): boolean {
    return this.drawerLayout && !!this.assetVin;
  }

  private loadVehicleState(): void {
    if (!this.showLocationRow) return;
    // The map dialog draws with the Google Maps script already on the page and loads none
    // itself. Every other screen that opens it asks for the script on the way in; so does
    // this one, so the first click on View on map does not wait on a download.
    this.googleMaps.addScriptToHeader().pipe(take(1), takeUntil(this.destroy$)).subscribe({ error: () => { } });
    this.vehicleDetail.getVehicleStateByVin(this.assetVin)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: state => { this.vehicleState = state ?? null; this.cdr.markForCheck(); },
        // The row still names the shop; only the position time and the map go without.
        error: () => { this.vehicleState = null; },
      });
  }

  // The same dialog the vehicle screen opens: one marker on the asset's position at the
  // zoom that screen uses. A missing or zero position shows the same warning it shows.
  openMap(): void {
    const event = vehicleMapEvent(this.vehicleState as VehicleState, this.translate, this.toast);
    if (!event) return;
    const { latitude, longitude } = event.vehicleState.position;
    // A second subscribe to a script already on the page answers at once. While the
    // script is still on its way, or when it fails to load, the dialog cannot be built,
    // and the reader hears that rather than nothing.
    const cannotOpen = () =>
      this.toast.show(this.translate.instant('@vehicleInvalidLocationWarningMessage'), { classname: 'warning' });
    this.googleMaps.addScriptToHeader().pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: loaded => {
        if (!loaded) { cannotOpen(); return; }
        this.dialogs.openMapDialog({
          options: { center: { lat: latitude, lng: longitude }, zoom: 15 },
          markerPositions: [{ lat: latitude, lng: longitude }],
        });
      },
      error: cannotOpen,
    });
  }

  // Raised once a disposition has been submitted, carrying what was sent. The accordion
  // ignores it — its own list-leave animation removes the card — but a drawer must close
  // itself, and the sheet marks the close in flight from the request it carries.
  @Output() dispositioned = new EventEmitter<DispositionRequest>();


  // Raised when a case write is accepted, carrying what it asserted. The sheet patches
  // its row from this instead of waiting out the projection lag.
  @Output() caseChanged = new EventEmitter<{
    caseId: string; assignedUserId: string; assignedLocationId: string;
  }>();

  @SelectSnapshot(TenantSessionState.getTenantFeatures) private tenantFeatures!: FeatureSettings;

  // Which cases gate this host wants. The Issues surfaces (the sheet's drawer, the vehicle
  // tab) read the Issues gate, which a tenant can turn off for Issues alone. The alert
  // triage dialog keeps the tenant's cases feature, because that dialog is out of scope
  // for the Issues switch this round; it passes nothing and gets the old gate.
  @Input() issueCasesGate = false;

  // Whether this panel shows cases. Read here rather than taken from the host: a host that
  // forgot to answer would offer a case to a tenant that has none.
  get showCase(): boolean {
    return this.issueCasesGate
      ? issueCasesEnabled(this.tenantFeatures)
      : isFeatureEnabled(this.tenantFeatures, TenantFeatures.WorkOrderCreation);
  }

  issue!: IssueDto;

  // False until the first real read lands. A seeded panel knows its header but not its
  // evidence, and saying "loading" there beats an empty section that reads as "none".
  loaded = false;

  evidenceGroups: EvidenceGroupVm[] = [];
  // How the Issue reached the confidence in its header. Null while the panel holds
  // only the projected row, which carries no history, and for an Issue whose history
  // is a single moment.
  series: ConfidenceSeriesVm | null = null;
  alertHits: AlertHit[] = [];
  alertHitsStatus = '';
  private alertHitsStarted = false;
  // contributionId -> reporter display name, for operator reports we can attribute.
  reporterNames: Record<string, string> = {};
  // The asset's live fault rows, for the evidence cards' bus-state line. Empty
  // until the lookup answers, and empty for good when it fails.
  assetFaults: AssetFault[] = [];
  timeline: IssueTimelineEntryVm[] = [];
  writeStatus: WriteStatus = WriteStatuses.Idle;

  // Disposition verdicts the template's buttons dispatch.
  readonly DispositionTypes = DispositionTypes;

  // The window the dialog opens on. The reader may change it inside the bounds, and what
  // they leave in the box is what travels, so this is a starting value and not the value
  // sent.
  readonly suppressionDays = SUPPRESSION_DAYS;

  coordinationOpen = false;
  bodyOpen = false;

  // Case workflow strategy for this issue: active case matched by asset + matchKey
  // (= issue id); creation carries the issue id; assignees filtered by asset groups.
  caseContext!: CaseWorkflowContext;

  // The asset the Issue is on, as somewhere to go. Rendered as a link only where the
  // reader is not already on that asset and holds the right to view it; without the right
  // the header still names the asset, because knowing which truck it is costs no
  // permission — only going there does.
  // Captured once, from the row that opened this panel. The Issue document carries its own
  // copy of the asset facts, taken when the Issue was written; the projected row is rebuilt
  // whenever the vehicle changes. Reading the live `issue` would therefore rename the asset
  // under the reader a second after opening, the moment the full document lands.
  private assetLabel = '';
  private assetVin = '';
  private assetFaults$: Observable<AssetFault[]> | null = null;

  get showsAsset(): boolean {
    return this.canOpenAsset && !!(this.assetLabel || this.assetVin);
  }

  get assetName(): string { return this.assetLabel || this.assetVin; }
  get assetTitle(): string { return this.assetVin; }

  get canLinkAsset(): boolean {
    return this.showsAsset
      && !!this.assetVin
      && this.authorization.hasRight(this.auth.user?.profile, ViewVehicle);
  }

  // Segments for a routerLink rather than a navigate call. The anchor gets an href of its
  // own, so middle-click and ctrl-click still open a tab — a reader working a list usually
  // wants the asset beside it, not instead of it — while a plain click routes inside the
  // application rather than reloading it.
  get assetLink(): unknown[] {
    return this.assetVin ? this.routing.vehicleLink(this.assetVin) : [];
  }


  // The asset's groups scope who a case may be assigned to. A host that holds them binds
  // the input; a host that has to fetch them calls this once they arrive, because the
  // drawer assigns inputs by direct property write and ngOnChanges never fires there.
  //
  // Without them the picker offers every user in the tenant, and the server then refuses
  // the assignment with "Assignee cannot see this issue" — an empty scope reads as "no
  // restriction", not as "nobody".
  setAssetGroups(groups: any[]): void {
    this.assetGroups = groups ?? [];
    this.caseContext = issueCaseWorkflowContext(
      this.alertDetailService, this.assetId, this.issueId, this.assetGroups);
    this.cdr.markForCheck();
  }

  // Warmed active-case read handed to the embedded case component so opening
  // Coordination costs no round trip.
  case$?: Observable<any>;
  private caseWarmConsumed = false;

  // Null until the warm read answers. A reader without the right to create a case sees
  // the panel only once the read confirms a case exists.
  caseAbsent: boolean | null = null;

  // Creating a case needs MaintainVehicle. Without it the panel has nothing to offer on an
  // Issue that has no case, so it shows only once the read confirms a case to look at.
  canCreateCase = false;

  get showCasePanel(): boolean {
    return this.showCase && !this.readOnly && (this.canCreateCase || this.caseAbsent === false);
  }

  @ViewChild(AlertCaseComponent) private caseWidget?: AlertCaseComponent;

  // The case panel is the only editor in here. The drawer guard reads this through the opener.
  get hasUnsavedEdits(): boolean {
    return this.caseWidget?.hasUnsavedEdits ?? false;
  }

  // The header close asks the same question the overlay does, so the two ways out agree.
  requestClose(): void {
    if (this.hasUnsavedEdits && !confirm(this.translate.instant('@confirmDiscardChanges'))) return;
    this.closeRequested.emit();
  }

  // Optional, and never a gate. Cleared once a verdict carries it.
  dispositionNote = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly state: IssuesStateService,
    private readonly issues: IssuesService,
    private readonly alertDetailService: AlertDetailService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly routing: SpaRoutingService,
    private readonly authorization: AuthorizationService,
    private readonly auth: AuthService,
    private readonly faults: AssetFaultService,
    private readonly alerts: AlertTriageOpener,
    private readonly vehicleDetail: VehicleDetailService,
    private readonly dialogs: DynamicDialogService,
    private readonly toast: ToastGlobalService,
    private readonly googleMaps: GoogleMapService,
    private readonly alertHistory: AlertService,
  ) {}

  ngOnInit(): void {
    this.canCreateCase = this.authorization.hasRight(this.auth.user?.profile, MaintainVehicle);
    this.caseContext = issueCaseWorkflowContext(
      this.alertDetailService, this.assetId, this.issueId, this.assetGroups ?? []);
    if (this.showCase && !this.readOnly) this.warmCase();

    if (this.startOpen) this.coordinationOpen = true;

    // Seeded before the subscription so the panel paints at once. A drawer host that
    // has no seed accepts one frame of empty rather than a wrong first paint.
    this.issue = (this.seed?.issue ?? this.seedIssue) as IssueDto;
    // Before any load can replace it.
    this.assetLabel = this.issue?.assetName ?? '';
    this.assetVin = this.issue?.assetVin ?? '';
    this.loadAssetFaults();
    this.loadVehicleState();
    // A widget seed is the full document; a projected seed is a header.
    this.loaded = !!this.seed?.issue;
    this.recompute();

    this.state.issuesFor(this.assetId).pipe(takeUntil(this.destroy$)).subscribe(list => {
      const found = list.find(i => i.id === this.issueId);
      if (found) { this.issue = found; this.loaded = true; }
      this.recompute();
      this.cdr.markForCheck();
    });
    this.state.writeStatusFor(this.issueId).pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.writeStatus = s;
      this.cdr.markForCheck();
    });

    // A drawer assigns its inputs by direct property write rather than through the
    // binding machinery, so the load has to be asked for here rather than waited for.
    //
    // History comes from a different place. The per-asset list serves open Issues only, so
    // a closed one is never in it: waiting on that list would leave the evidence rack
    // shimmering for ever and the activity empty. Read it by its own id instead.
    if (this.readOnly) {
      this.issues.getById(this.issueId).pipe(take(1), takeUntil(this.destroy$)).subscribe({
        next: issue => {
          if (issue) { this.issue = issue; this.loaded = true; }
          this.recompute();
          this.cdr.markForCheck();
        },
        // The seed still names the Issue, so a failed read leaves the header standing
        // rather than an empty panel.
        error: () => { this.loaded = true; this.recompute(); this.cdr.markForCheck(); },
      });
      return;
    }

    this.state.load(this.assetId);
  }

  // One lookup per open Issue, shared by every evidence card in it. The card
  // renders without it, so a failure costs the bus-state line and nothing else.
  private loadAssetFaults(): void {
    if (!this.assetVin) return;
    this.assetFaults$ = this.assetFaults$ ?? this.faults.getAssetFaultState(this.assetVin);
    this.assetFaults$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: faults => { this.assetFaults = faults ?? []; this.cdr.markForCheck(); },
      error: () => { this.assetFaults = []; this.cdr.markForCheck(); },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private recompute(): void {
    if (!this.issue) return;
    const groups = groupEvidence(this.issue.contributions);
    this.evidenceGroups = groups;
    this.timeline = buildTimeline(this.issue, id => this.state.resolveUserName(id), { plain: this.drawerLayout });
    this.reporterNames = manualReporterNames(this.issue, id => this.state.resolveUserName(id));
    this.series = buildConfidenceSeries(this.issue, Date.now());
    this.loadChartAlertHits();
  }

  private loadChartAlertHits(): void {
    if (this.alertHitsStarted || !this.loaded || !this.drawerLayout || !this.showConfidenceChart
      || !this.series || !this.assetVin
      || !this.authorization.hasRight(this.auth.user?.profile, ViewAlert)) return;
    const scopes = alertHitScopes(this.issue.contributions);
    if (!scopes.length) return;
    this.alertHitsStarted = true;
    this.alertHitsStatus = '';
    loadAlertHits(this.alertHistory, this.assetVin, scopes).pipe(takeUntil(this.destroy$)).subscribe({
      next: result => {
        this.alertHits = result.hits;
        this.alertHitsStatus = result.failed ? '@issueChartAlertsUnavailable'
          : result.limited ? '@issueChartAlertsLimited' : '';
        this.cdr.markForCheck();
      },
    });
  }

  // The document's own update time moves on a case edit and on an aging tick, neither
  // of which is a firing. Read the newest contribution instead.
  get lastFiredUtc(): string { return this.issue ? lastActivityUtc(this.issue) : ''; }

  get confidencePct(): number { return displayConfidencePct(this.issue); }
  get confidenceMuted(): boolean { return isConfidenceMuted(this.issue?.confidence ?? 0); }
  get reopened(): boolean { return isReopened(this.issue); }
  get dvirOrigin(): boolean { return isDvirOrigin(this.issue); }
  get submitting(): boolean { return this.writeStatus === WriteStatuses.Submitting; }

  get bodyVisible(): boolean { return !this.collapsibleBody || this.bodyOpen; }

  toggleCoordination(): void {
    this.coordinationOpen = !this.coordinationOpen;
    if (!this.coordinationOpen || !this.showCase) return;
    // Re-open re-reads: the buffered response predates anything the previous open wrote,
    // and the replay would hand the child that stale snapshot.
    if (this.caseWarmConsumed) this.warmCase();
    this.caseWarmConsumed = true;
  }

  // Read the active case up front so Coordination opens without a round trip. The replay
  // is lazy — the subscribe is what issues the request. Errors are ignored here and
  // re-read by the child.
  private warmCase(): void {
    this.case$ = this.caseContext.getActiveCase()
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    // The read emits the case-list response; the widget takes the first. An empty list
    // is a known absence.
    this.case$.pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.caseAbsent = !(response && response.length > 0 && response[0]);
        this.cdr.markForCheck();
      },
      error: () => { },
    });
  }

  // The case workflow is what usually fills Coordination, and it renders only while the
  // feature is on and the Issue is not history. In the classic layout the activity shares
  // the section, so the section stays while either has something to show. In the drawer
  // the activity has a section of its own, and Coordination is the case alone: it goes
  // with the case, which is what a tenant without cases expects.
  get showCoordination(): boolean {
    if (this.drawerLayout) return this.showCasePanel;
    return this.showCasePanel || this.timeline.length > 0;
  }

  get showIssueHistory(): boolean {
    return this.drawerLayout && this.timeline.length > 0;
  }

  get showConfidenceChart(): boolean {
    return !this.drawerLayout || !isFeatureEnabled(this.tenantFeatures, TenantFeatures.HideIssueConfidenceChart);
  }

  get canShowFaults(): boolean {
    return !!this.assetVin && (!this.drawerLayout
      || !isFeatureEnabled(this.tenantFeatures, TenantFeatures.HideIssueFaultHistory));
  }

  openFaultHistory(): void {
    this.dialog.open(FaultHistoryDialogComponent, {
      data: {
        assetId: this.assetId,
        vin: this.assetVin,
        assetName: this.assetLabel,
      } as FaultHistoryDialogData,
      panelClass: 'fh-dialog',
      width: '900px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  // Evidence links to what fired it. An Alert opens the alert quick-view; a DVIR defect
  // opens the inspection it came from. Both are dialogs, so the Issue stays open under
  // them.

  onOpenSource(contribution: ContributionDto): void {
    if (contribution.sourceType === ContributionSourceTypes.Alert && contribution.sourceRefAlertId) {
      this.alerts.show({
        alert: {
          alertRuleId: contribution.ruleId,
          latestAlertId: contribution.sourceRefAlertId,
          subject: contribution.displayLabel,
          severity: this.translate.instant(severityLabelKey(contribution.metrics?.severityAsserted ?? 0)),
          count: contribution.fireCount || 1,
          lastFiredAtUtc: contribution.lastFiredAt,
        },
        vehicleName: this.issue?.assetName,
        vin: this.issue?.assetVin,
        assetId: this.issue?.assetId,
      } as AlertDetailDialogData);
      return;
    }
    if (contribution.sourceType === ContributionSourceTypes.DVIRDefect && contribution.sourceRefDvirId) {
      this.dialog.open(DvirDetailDialogComponent, {
        data: { dvirId: contribution.sourceRefDvirId } as DvirDetailDialogData,
        panelClass: 'sc-alert-dialog',
        width: '560px',
        maxWidth: '95vw',
        autoFocus: false,
      });
    }
  }

  // One step. The verdict carries whatever note the user chose to type, and sends at
  // once — a second click to confirm reverses nothing, so it only costs time.
  dispose(type: OperatorDispositionType): void {
    if (this.submitting) return;

    // The two verdicts that can buy quiet ask first. Fixed does not: it carries its own
    // reopen behaviour, where a return before the asset has moved much is the same problem
    // rather than a new one, and a quiet window would hide exactly that.
    if (type === DispositionTypes.FalsePositive || type === DispositionTypes.Dismissed) {
      this.askSuppression(type);
      return;
    }

    this.send(type);
  }

  private askSuppression(type: OperatorDispositionType): void {
    this.dialog.open(SuppressDialogComponent, {
      data: { days: this.suppressionDays },
      width: '420px',
      autoFocus: true,
    })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result: SuppressDialogResult | undefined) => {
        // Escape and the backdrop both land here. Neither is an answer, so the Issue stays
        // open rather than closing without the window the reader never declined.
        if (!result) return;
        this.send(type, result.suppress ? result.days : 0);
      });
  }

  private send(type: OperatorDispositionType, suppressionDurationDays = 0): void {
    const request: DispositionRequest = { dispositionType: type };
    // Zero is the same as sending nothing: the writer expires the window at the moment of
    // the close, so the next signal opens a fresh Issue.
    if (suppressionDurationDays > 0) request.suppressionDurationDays = suppressionDurationDays;
    const cause = this.dispositionNote.trim();
    if (cause) request.cause = cause;
    this.close(request);
  }

  private close(request: DispositionRequest): void {
    this.state.closeIssue(this.assetId, this.issueId, request);
    this.dispositionNote = '';
    this.dispositioned.emit(request);
  }
}
